import { useState } from 'react';

interface ConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function ConsentModal({ isOpen, onClose, onAccept }: ConsentModalProps) {
  const [consents, setConsents] = useState({
    informational: false,
    notSubstitute: false,
    privacy: false,
    recording: false,
    terms: false,
    disclaimer: false,
  });

  if (!isOpen) return null;

  const allAccepted = Object.values(consents).every(v => v);
  const acceptedCount = Object.values(consents).filter(v => v).length;
  const totalCount = Object.keys(consents).length;
  const progress = (acceptedCount / totalCount) * 100;

  const handleCheckboxChange = (key: keyof typeof consents) => {
    setConsents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAccept = () => {
    if (allAccepted) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Consent & Agreement</h2>
          <p className="text-sm text-gray-600">Please review and accept all items below to proceed with scheduling</p>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-700">{acceptedCount} of {totalCount} accepted</span>
              <span className="text-xs text-gray-500">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-teal-500 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="space-y-6">
            {/* Informational Purpose */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.informational}
                  onChange={() => handleCheckboxChange('informational')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">Informational Purpose</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I understand that this consultation is for educational and informational purposes only, based on personal experience and independent research.
                </p>
              </div>
            </label>

            {/* Not a Substitute */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.notSubstitute}
                  onChange={() => handleCheckboxChange('notSubstitute')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">Not a Substitute for Medical Care</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I acknowledge that this is not medical advice, diagnosis, or treatment. No doctor-patient relationship is created. I will consult my licensed healthcare providers for all medical decisions.
                </p>
              </div>
            </label>

            {/* Privacy & Data Use */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.privacy}
                  onChange={() => handleCheckboxChange('privacy')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">Privacy & Data Use</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I understand that my information will be handled according to the privacy policy, and I should not share sensitive medical records unless comfortable with electronic communication risks.
                </p>
              </div>
            </label>

            {/* Session Recording */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.recording}
                  onChange={() => handleCheckboxChange('recording')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">Session Recording</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I consent to the session being recorded for quality assurance and educational purposes.
                </p>
              </div>
            </label>

            {/* Terms & Conditions */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.terms}
                  onChange={() => handleCheckboxChange('terms')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">Terms & Conditions</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I agree to the terms of service and understand the scope and limitations of this consultation.
                </p>
              </div>
            </label>

            {/* Medical Disclaimer - Full Text */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={consents.disclaimer}
                  onChange={() => handleCheckboxChange('disclaimer')}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Medical Information & Peer Support Disclaimer</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                  <p className="text-xs font-semibold text-gray-700 mb-3">Effective date: February 19, 2026</p>
                  
                  <div className="space-y-4 text-xs text-gray-700 leading-relaxed">
                    <p>
                      This disclaimer applies to any educational information, peer support, coaching-style conversations, and resources I provide regarding heart health, familial hypercholesterolemia (FH), recovery, or related topics. It is meant to clarify boundaries, reduce misunderstandings, and help you make safe decisions with your licensed clinicians.
                    </p>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Not medical care</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>I am not a physician, nurse, therapist, or other licensed healthcare professional, and I do not provide medical care.</li>
                        <li>Any information I share (in person, by phone/video, email, chat, documents, website content, or otherwise) is provided only as general educational and informational content based on my personal experience and my own independent research.</li>
                        <li>Nothing I share is intended to be, or should be construed as, medical advice, diagnosis, treatment, prescription, or a substitute for professional medical judgment.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">No doctor-patient relationship</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>My communications with you do not create a doctor-patient relationship (or any other healthcare provider-patient relationship).</li>
                        <li>I am not acting as your clinician, and I will not manage your medical care.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Always consult your own clinicians</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>You must consult your primary care provider and/or your own specialist physicians for personal medical advice, diagnosis, or treatment decisions.</li>
                        <li>Never disregard, delay, or change medical care because of anything you received from me.</li>
                        <li>Do not start, stop, or change medications, supplements, exercise, diet, or any treatment plan without first discussing with your licensed healthcare providers.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Emergency and urgent situations</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>If you believe you are experiencing a medical emergency, call 911 (or your local emergency number) immediately or go to the nearest emergency department.</li>
                        <li>Do not use my information or communications for emergency decision-making.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">No guarantees; results vary</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Health outcomes are individualized. I do not guarantee any specific results, improvements, or outcomes.</li>
                        <li>Any examples, stories, or testimonials are not promises of results and may not reflect typical outcomes.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Assumption of risk</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>You acknowledge that applying health-related information involves inherent risks, including the risk of serious harm.</li>
                        <li>If you choose to act on any information, you do so voluntarily and at your own risk, and you remain solely responsible for your decisions and outcomes.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Limitations of scope</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>I may discuss general topics such as cardiovascular risk factors, lifestyle considerations, patient self-advocacy, questions to ask your care team, and ways to navigate the healthcare system.</li>
                        <li>I do not interpret lab results, imaging, or diagnostics; I do not provide individualized clinical recommendations; and I do not advise on suitability of procedures, medications, or specific treatment choices.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Third-party information and links</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>I may reference third-party materials (articles, guidelines, tools, apps, websites, or organizations). Such references are for convenience only and do not constitute endorsement.</li>
                        <li>I do not control third-party content and cannot guarantee its accuracy, completeness, or currency.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Confidentiality and communications</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Do not send me sensitive personal information (including medical records) unless you are comfortable with the privacy risks of electronic communications.</li>
                        <li>I do not guarantee confidentiality, and I am not subject to healthcare privacy laws that apply to licensed providers (such as HIPAA) unless separately agreed in writing.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Limitation of liability</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>To the maximum extent permitted by law, you agree that I will not be liable for any direct, indirect, incidental, consequential, special, exemplary, or punitive damages arising out of or related to your use of (or reliance on) any information I provide.</li>
                        <li>This includes, without limitation, damages for personal injury, illness, complications, lost profits, lost data, service interruption, or emotional distress, even if I have been advised of the possibility of such damages.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Indemnification</h4>
                      <p>You agree to indemnify and hold me harmless from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys&apos; fees) arising from your misuse of the information, your violation of this disclaimer, or your actions/omissions.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Not legal/financial advice</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>I am not an attorney, financial advisor, or insurance professional. Any discussions of workplace benefits, coverage, costs, or claims are general information only and not legal, financial, or insurance advice.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Use by adults; parental guidance</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>My content is intended for adults. If you are under 18, a parent/guardian must be involved and should consult a licensed clinician on your behalf.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Jurisdiction and severability</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>This disclaimer is intended to be interpreted as broadly as permitted by applicable law. If any provision is found unenforceable, the remaining provisions will remain in effect.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Acknowledgment</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>By receiving information from me, you acknowledge that you have read, understood, and agree to this disclaimer.</li>
                        <li>If you do not agree, do not use or rely on my information.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Please scroll through and read the complete disclaimer above before accepting.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={!allAccepted}
              className={`px-8 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                allAccepted
                  ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-lg shadow-teal-500/30'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {allAccepted ? 'Confirm & Schedule' : `Accept All Items (${acceptedCount}/${totalCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
